<template>
  <div
    class="ui-select ui-searchable-select"
    ref="selectRootRef"
    :data-searchable-select-id="uniqueId"
    :class="[
      `ui-select--${size}`,
      {
        'ui-select--open': isOpen,
        'ui-select--selected': selectedValue && selectedValue !== '',
        'ui-select--error': error,
        'ui-select--disabled': disabled,
        'ui-select--view': viewOnly,
      },
    ]"
    :style="{ width }"
  >
    <!-- 셀렉트 헤더 부분 (선택된 값 표시) -->
    <div
      class="ui-select__header"
      @click="toggleDropdown"
      @blur="handleHeaderBlur"
      :tabindex="disabled || viewOnly ? -1 : 0"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
    >
      <div class="ui-select__value">
        <template v-if="multiple">
          <span v-if="Array.isArray(modelValue) && modelValue.length > 0">
            {{ multipleDisplayLabel }}
          </span>
          <span v-else class="ui-select__placeholder">{{ displayPlaceholder }}</span>
        </template>
        <template v-else>
          <span
            v-if="selectedOption"
            v-ellipsis-tooltip="getEllipsisTooltip(selectedOption.label)"
            class="ui-select__selected-text"
          >
            {{ selectedOption.label }}
          </span>
          <span v-else class="ui-select__placeholder">{{ displayPlaceholder }}</span>
        </template>
      </div>
      <i class="ui-select__icon" :class="{ 'ui-select__icon--up': isOpen }"></i>
    </div>

    <!-- 드롭다운 부분 (검색창 + 옵션 목록) — teleport 시 body로 렌더 (UiSelect와 동일: 열릴 때만 마운트) -->
    <Teleport to="body" v-if="isOpen && useBodyTeleport">
      <div
        ref="dropdownRef"
        class="ui-select__dropdown ui-searchable-select__dropdown ui-searchable-select__dropdown--teleported"
        :class="{ 'ui-select__dropdown--up': dropdownDirection === 'up' }"
        :style="dropdownStyles"
        @mousedown.prevent
      >
        <!-- 검색 입력창 -->
        <div class="ui-select__search-container">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="ui-select__search-input"
            :placeholder="searchPlaceholder"
            @input="handleSearchInput"
            @keydown="handleSearchKeydown"
            @click.stop
          />
        </div>

        <!-- 옵션 목록 (draggable: 검색 중에는 filtered/ordered 인덱스 불일치로 DnD 비활성) -->
        <div class="ui-select__options" role="listbox" @dragover.prevent @drop="handleDrop">
          <div
            v-for="option in filteredOptions"
            :key="option.value"
            class="ui-select__option"
            :class="{
              'ui-select__option--selected': multiple ? isMultipleOptionChecked(option) : selectedValue === option.value,
              'ui-select__option--draggable': canDragReorder,
              'ui-select__option--drag-over': canDragReorder && dragOverValue === option.value,
              'ui-select__option--dragging': canDragReorder && draggedOption?.value === option.value,
            }"
            :draggable="canDragReorder"
            @dragstart="handleDragStart(option, $event)"
            @dragend="handleDragEnd"
            @dragover.prevent="handleDragOver(option, $event)"
            @dragleave="handleDragLeave(option, $event)"
            @click="selectOption(option)"
          >
            <template v-if="multiple">
              <input type="checkbox" class="ui-select__checkbox" :checked="isMultipleOptionChecked(option)" @change.stop />
            </template>
            <span v-ellipsis-tooltip="getEllipsisTooltip(option.label)" class="ui-select__option-label">{{ option.label }}</span>
          </div>
          <div v-if="filteredOptions.length === 0" class="ui-select__no-results">{{ noResultsLabel }}</div>
        </div>
      </div>
    </Teleport>
    <div
      v-if="!useBodyTeleport"
      v-show="isOpen"
      ref="dropdownRef"
      class="ui-select__dropdown ui-searchable-select__dropdown"
      :class="{ 'ui-select__dropdown--up': forceUpward }"
    >
      <!-- 검색 입력창 -->
      <div class="ui-select__search-container">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="ui-select__search-input"
          :placeholder="searchPlaceholder"
          @input="handleSearchInput"
          @keydown="handleSearchKeydown"
          @click.stop
        />
      </div>

      <!-- 옵션 목록 (draggable: 검색 중에는 filtered/ordered 인덱스 불일치로 DnD 비활성) -->
      <div class="ui-select__options" role="listbox" @dragover.prevent @drop="handleDrop">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="ui-select__option"
          :class="{
            'ui-select__option--selected': multiple ? isMultipleOptionChecked(option) : selectedValue === option.value,
            'ui-select__option--draggable': canDragReorder,
            'ui-select__option--drag-over': canDragReorder && dragOverValue === option.value,
            'ui-select__option--dragging': canDragReorder && draggedOption?.value === option.value,
          }"
          :draggable="canDragReorder"
          @dragstart="handleDragStart(option, $event)"
          @dragend="handleDragEnd"
          @dragover.prevent="handleDragOver(option, $event)"
          @dragleave="handleDragLeave(option, $event)"
          @click="selectOption(option)"
        >
          <template v-if="multiple">
            <input type="checkbox" class="ui-select__checkbox" :checked="isMultipleOptionChecked(option)" @change.stop />
          </template>
          <span v-ellipsis-tooltip="getEllipsisTooltip(option.label)" class="ui-select__option-label">{{ option.label }}</span>
        </div>
        <div v-if="filteredOptions.length === 0" class="ui-select__no-results">{{ noResultsLabel }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { UI_OVERLAY_DISMISS_EVENT } from '~/composables/useOverlayDismiss';

/** multiple + 전체(value '') 사용 시: ['']만 있거나 모든 보드 id가 있으면 전체 선택으로 간주 */
const getIsEveryItemVisuallySelected = (modelValue, options) => {
  if (!Array.isArray(modelValue) || modelValue.length === 0) return false;
  const ids = options.filter(o => o.value !== '').map(o => o.value);
  if (ids.length === 0) {
    return modelValue.includes('');
  }
  const hasEmpty = modelValue.includes('');
  const allIdsIn = ids.every(id => modelValue.includes(id));
  if (hasEmpty && modelValue.filter(v => v !== '').length === 0) return true;
  if (hasEmpty && allIdsIn) return true;
  if (!hasEmpty && allIdsIn) return true;
  return false;
};

const props = defineProps({
  placeholder: {
    type: String,
    default: '',
  },
  options: {
    type: Array,
    default: () => [
      { value: '1', label: '옵션 1' },
      { value: '2', label: '옵션 2' },
      { value: '3', label: '옵션 3' },
      { value: '4', label: '옵션 4' },
      { value: '5', label: '옵션 5' },
      { value: '6', label: '옵션 6' },
      { value: '7', label: '옵션 7' },
      { value: '8', label: '옵션 8' },
      { value: '9', label: '옵션 9' },
      { value: '10', label: '옵션 10' },
    ],
  },
  modelValue: {
    type: [String, Number, Array],
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  viewOnly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value),
  },
  width: {
    type: [String, Number],
    default: '150px',
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  /** true: 바깥 스크롤 시 드롭다운 닫기 (모달 내 목록 스크롤 등) */
  closeOnScroll: {
    type: Boolean,
    default: false,
  },
  /** true: 드롭다운을 body로 렌더링(모달 overflow·뷰포트 하단에서 잘림 방지) */
  teleportToBody: {
    type: Boolean,
    default: false,
  },
  /** @deprecated teleportToBody 사용 */
  teleport: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: [String, Number],
    default: 300,
  },
  forceUpward: {
    type: Boolean,
    default: false,
  },
  /** true: 말줄임 여부와 관계없이 라벨 툴팁 항상 표시 */
  forceTooltip: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change', 'dragstart', 'dragend', 'drop']);

const getEllipsisTooltip = label => {
  if (props.forceTooltip) {
    return { text: label ?? '', force: true };
  }
  return label ?? '';
};

const { t, locale } = useI18n();

const displayPlaceholder = computed(() => props.placeholder || t('common.selectPlaceholder'));
const searchPlaceholder = computed(() => t('common.searchableSelect.searchPlaceholder'));
const noResultsLabel = computed(() => t('common.searchableSelect.noResults'));
const allLabel = computed(() => t('common.action.all'));

const multipleDisplayLabel = computed(() => {
  locale.value;

  if (!props.multiple || !Array.isArray(props.modelValue) || props.modelValue.length === 0) {
    return '';
  }

  const actualOptions = props.options.filter(o => o.value !== '');
  const selectedOptions = actualOptions.filter(o => props.modelValue.includes(o.value));

  if (props.modelValue.includes('') && selectedOptions.length === 0) {
    return allLabel.value;
  }

  if (selectedOptions.length === actualOptions.length && actualOptions.length > 0) {
    return allLabel.value;
  }

  if (selectedOptions.length === 0) {
    return '';
  }

  const labels = selectedOptions.map(o => o.label);
  return labels.length > 1
    ? t('common.searchableSelect.selectedSummary', { first: labels[0], rest: labels.length - 1 })
    : labels[0] || '';
});

/** teleport prop 하위 호환 */
const useBodyTeleport = computed(() => props.teleportToBody || props.teleport);

// 상태 관리
const isOpen = ref(false);
const searchQuery = ref('');
const selectedValue = ref(props.modelValue);
const searchInput = ref(null);
const selectRootRef = ref(null);
const dropdownRef = ref(null);
const uniqueId = ref(`select-${Math.random().toString(36).substr(2, 9)}`);

const dropdownDirection = ref('down');
const dropdownStyles = ref({});

let dropdownPositionRafId = null;
let selectResizeObserver = null;

const parseMaxHeightProp = () => {
  const m = props.maxHeight;
  return typeof m === 'number' ? m : parseInt(String(m), 10) || 300;
};

const cancelScheduledDropdownPosition = () => {
  if (dropdownPositionRafId != null) {
    cancelAnimationFrame(dropdownPositionRafId);
    dropdownPositionRafId = null;
  }
};

const detachSelectResizeObserver = () => {
  selectResizeObserver?.disconnect();
  selectResizeObserver = null;
};

const attachSelectResizeObserver = () => {
  detachSelectResizeObserver();
  if (!selectRootRef.value || typeof ResizeObserver === 'undefined') return;
  selectResizeObserver = new ResizeObserver(() => {
    if (isOpen.value && useBodyTeleport.value) calculateDropdownPosition();
  });
  selectResizeObserver.observe(selectRootRef.value);
};

const scheduleDropdownPosition = () => {
  if (!useBodyTeleport.value) return;
  cancelScheduledDropdownPosition();
  nextTick(() => {
    if (!isOpen.value) return;
    calculateDropdownPosition();
    dropdownPositionRafId = requestAnimationFrame(() => {
      dropdownPositionRafId = null;
      if (!isOpen.value) return;
      calculateDropdownPosition();
      // 위치 확정 후 검색창 포커스 (포커스로 인한 scroll → closeOnScroll 오동작 방지)
      if (searchInput.value) {
        searchInput.value.focus();
        searchInput.value.select();
      }
    });
  });
};

const resolveDropdownZIndex = () => {
  const el = selectRootRef.value;
  if (!el) return 940;

  const filterPanel = el.closest('.filter-dropdown');
  if (filterPanel) {
    const z = parseInt(window.getComputedStyle(filterPanel).zIndex, 10);
    return (Number.isFinite(z) ? z : 10051) + 20;
  }

  const popup = el.closest('.ui-popup');
  if (popup) {
    let z = parseInt(window.getComputedStyle(popup).zIndex, 10);
    if (!Number.isFinite(z)) {
      const content = popup.querySelector('.ui-popup__content');
      if (content) {
        z = parseInt(window.getComputedStyle(content).zIndex, 10);
      }
    }
    const base = Number.isFinite(z) ? z : 1000;
    return base + 10;
  }

  const slidePanel = el.closest('.detail-panel');
  if (slidePanel) {
    const z = parseInt(window.getComputedStyle(slidePanel).zIndex, 10);
    const base = Number.isFinite(z) ? z : 1000;
    return base + 10;
  }

  return 940;
};

const calculateDropdownPosition = () => {
  if (!useBodyTeleport.value || !isOpen.value || !selectRootRef.value) return;

  const selectRect = selectRootRef.value.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  const preferredMax = parseMaxHeightProp();
  const searchBarHeight = 52;
  const gap = 4;
  const minVisible = 100;

  const headerEl = document.querySelector('.header');
  const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;

  const optCount = Math.max(filteredOptions.value.length, 1);
  const estimatedOptionsHeight = Math.min(optCount * 40 + 8, preferredMax - searchBarHeight);
  const estimatedDropdownHeight = Math.min(searchBarHeight + estimatedOptionsHeight, preferredMax);

  const spaceBelow = Math.max(0, windowHeight - selectRect.bottom - gap);
  const spaceAbove = Math.max(0, selectRect.top - gap - headerBottom);

  let direction = 'down';
  if (props.forceUpward) {
    direction = 'up';
  } else if (spaceBelow < estimatedDropdownHeight || spaceBelow < minVisible) {
    if (spaceAbove >= spaceBelow || spaceAbove >= minVisible) {
      direction = 'up';
    }
  }

  const downMax = Math.min(spaceBelow, preferredMax);
  const upMax = Math.min(spaceAbove, preferredMax);
  if (direction === 'down' && downMax < minVisible && upMax > downMax) {
    direction = 'up';
  }

  dropdownDirection.value = direction;

  const styles = {
    position: 'fixed',
    left: `${selectRect.left}px`,
    width: `${selectRect.width}px`,
    zIndex: resolveDropdownZIndex(),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  if (direction === 'up') {
    styles.bottom = `${Math.max(gap, windowHeight - selectRect.top + gap)}px`;
    styles.maxHeight = `${Math.max(minVisible, upMax)}px`;
    delete styles.top;
  } else {
    styles.top = `${selectRect.bottom + gap}px`;
    styles.maxHeight = `${Math.max(minVisible, downMax)}px`;
    delete styles.bottom;
  }

  if (selectRect.right > windowWidth) {
    styles.right = '8px';
    delete styles.left;
  }

  dropdownStyles.value = styles;
};

// 옵션 순서 관리
const orderedOptions = ref([...props.options]);

const isEveryItemVisuallySelected = computed(() => {
  if (!props.multiple) return false;
  return getIsEveryItemVisuallySelected(props.modelValue, props.options);
});

const isMultipleOptionChecked = option => {
  if (!props.multiple || !Array.isArray(props.modelValue)) return false;
  if (isEveryItemVisuallySelected.value) return true;
  return props.modelValue.includes(option.value);
};

// 선택된 옵션 객체
const selectedOption = computed(() => {
  return orderedOptions.value.find(option => option.value === selectedValue.value);
});

// 검색 입력 핸들러
const handleSearchInput = event => {
  searchQuery.value = event.target.value;
};

/** 검색창 Enter: 필터된 첫 번째 항목 선택, Escape: 드롭다운 닫기 */
const handleSearchKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    isOpen.value = false;
    searchQuery.value = '';
    return;
  }

  if (event.key !== 'Enter') return;

  event.preventDefault();
  event.stopPropagation();

  if (!String(searchQuery.value ?? '').trim()) return;

  const options = filteredOptions.value;
  if (!options.length) return;

  selectOption(options[0]);
};

// 검색 필터링된 옵션
const filteredOptions = computed(() => {
  if (!searchQuery.value) return orderedOptions.value;

  return orderedOptions.value.filter(option => option.label.toLowerCase().includes(searchQuery.value.toLowerCase()));
});

/** 검색 중에는 DnD 비활성 (filtered 인덱스 ≠ ordered 인덱스) */
const canDragReorder = computed(() => props.draggable && !String(searchQuery.value ?? '').trim());

// 드롭다운 토글
const toggleDropdown = () => {
  if (props.disabled || props.viewOnly) return;

  if (!isOpen.value) {
    // 다른 셀렉트 드롭다운을 닫기 위해 이벤트 발생
    window.dispatchEvent(
      new CustomEvent('select-dropdown-toggle', {
        detail: { id: uniqueId.value },
      }),
    );
  }

  isOpen.value = !isOpen.value;
};

// 옵션 선택
const selectOption = option => {
  if (props.disabled || props.viewOnly) return;
  if (skipNextSelectClick.value) {
    skipNextSelectClick.value = false;
    return;
  }

  if (props.multiple) {
    let current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    // 전체 선택 기능
    if (option.value === '') {
      current = ['']; // 전체 선택 시 ''로

      emit('update:modelValue', current);
      emit('change', current);
      return;
    }
    // 일반 선택 (토글)
    const index = current.indexOf(option.value);
    if (index > -1) current.splice(index, 1);
    else current.push(option.value);

    //  전체가 포함돼 있다면 항상 전체 제거 (혼합 방지)
    if (current.includes('')) {
      current = current.filter(o => o !== '');
    }

    emit('update:modelValue', current);
    emit('change', current);
  } else {
    emit('update:modelValue', option.value);
    emit('change', option);
    isOpen.value = false;
  }

  if (!props.multiple) {
    isOpen.value = false;
  }

  searchQuery.value = '';
};

// 다른 셀렉트가 열릴 때 현재 셀렉트를 닫는 이벤트 핸들러
const handleOtherSelectOpen = event => {
  if (event.detail.id !== uniqueId.value && isOpen.value) {
    isOpen.value = false;
    searchQuery.value = '';
  }
};

// 외부 클릭 감지 (UiSelect와 동일: dropdownRef 미마운트 시 즉시 닫지 않음)
const handleDocumentClick = event => {
  if (
    isOpen.value &&
    selectRootRef.value &&
    !selectRootRef.value.contains(event.target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target)
  ) {
    isOpen.value = false;
    searchQuery.value = '';
  }
};

const handleHeaderBlur = () => {
  setTimeout(() => {
    if (!isOpen.value) return;
    const active = document.activeElement;
    if (selectRootRef.value?.contains(active) || dropdownRef.value?.contains(active)) return;
    isOpen.value = false;
    searchQuery.value = '';
  }, 0);
};

const handleOverlayDismiss = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  searchQuery.value = '';
};

const handleScrollWhileOpen = event => {
  if (!isOpen.value) return;
  if (props.closeOnScroll) {
    if (dropdownRef.value?.contains(event.target)) return;
    if (selectRootRef.value?.contains(event.target)) return;
    isOpen.value = false;
    searchQuery.value = '';
    return;
  }
  if (useBodyTeleport.value) {
    calculateDropdownPosition();
  }
};

// 드롭다운 열릴 때 위치 계산·리스너 등록
watch(isOpen, newValue => {
  if (newValue) {
    scheduleDropdownPosition();
    attachSelectResizeObserver();
    window.addEventListener('resize', calculateDropdownPosition);
    // closeOnScroll: 위치 확정(rAF) 이후에 등록해 열자마자 닫히는 현상 방지
    if (props.closeOnScroll) {
      requestAnimationFrame(() => {
        if (isOpen.value) {
          window.addEventListener('scroll', handleScrollWhileOpen, true);
        }
      });
    } else {
      window.addEventListener('scroll', handleScrollWhileOpen, true);
    }
    // teleport가 아닐 때만 즉시 포커스 (teleport는 scheduleDropdownPosition rAF에서 처리)
    if (!useBodyTeleport.value) {
      nextTick(() => {
        if (searchInput.value) {
          searchInput.value.focus();
          searchInput.value.select();
        }
      });
    }
  } else {
    searchQuery.value = '';
    cancelScheduledDropdownPosition();
    detachSelectResizeObserver();
    window.removeEventListener('resize', calculateDropdownPosition);
    window.removeEventListener('scroll', handleScrollWhileOpen, true);
  }
});

watch(
  () => filteredOptions.value.length,
  () => {
    if (useBodyTeleport.value && isOpen.value) {
      nextTick(() => calculateDropdownPosition());
    }
  },
);

// 이벤트 리스너 등록 및 해제
onMounted(() => {
  document.addEventListener('click', handleDocumentClick, true);
  window.addEventListener('select-dropdown-toggle', handleOtherSelectOpen);
  window.addEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
});

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick, true);
  window.removeEventListener('select-dropdown-toggle', handleOtherSelectOpen);
  window.removeEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  window.removeEventListener('resize', calculateDropdownPosition);
  window.removeEventListener('scroll', handleScrollWhileOpen, true);
  cancelScheduledDropdownPosition();
  detachSelectResizeObserver();
});

// modelValue 변경 감지
watch(
  () => props.modelValue,
  newValue => {
    selectedValue.value = newValue;
  },
  { immediate: true },
);

// options 변경 감지
watch(
  () => props.options,
  newOptions => {
    orderedOptions.value = [...newOptions];
  },
  { deep: true },
);

const draggedOption = ref(null);
const dragOverValue = ref(null);
const skipNextSelectClick = ref(false);

const handleDragStart = (option, event) => {
  if (!canDragReorder.value) return;

  draggedOption.value = option;
  dragOverValue.value = null;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(option.value));

  emit('dragstart', option);
};

const handleDragEnd = () => {
  if (!props.draggable) return;

  draggedOption.value = null;
  dragOverValue.value = null;
  emit('dragend');
};

const handleDragOver = (option, event) => {
  if (!canDragReorder.value || !draggedOption.value) return;

  event.preventDefault();
  dragOverValue.value = option.value;
};

const handleDragLeave = (option, event) => {
  if (!canDragReorder.value) return;

  const related = event.relatedTarget;
  if (related && event.currentTarget.contains(related)) return;
  if (dragOverValue.value === option.value) dragOverValue.value = null;
};

const handleDrop = event => {
  if (!canDragReorder.value) return;

  event.preventDefault();

  const drag = draggedOption.value;
  const overVal = dragOverValue.value;

  if (!drag || overVal === null || overVal === undefined) {
    dragOverValue.value = null;
    return;
  }

  const draggedVal = drag.value;
  if (draggedVal === overVal) {
    dragOverValue.value = null;
    return;
  }

  const newOrder = [...orderedOptions.value];
  const fromIdx = newOrder.findIndex(o => o.value === draggedVal);
  const toIdx = newOrder.findIndex(o => o.value === overVal);

  if (fromIdx === -1 || toIdx === -1) {
    dragOverValue.value = null;
    return;
  }

  // 송기준 수정: 대상 옵션의 아래쪽에 드롭하면 뒤에 삽입해 마지막 순서 이동을 허용
  const targetEl = event.target.closest('.ui-select__option');
  const targetRect = targetEl?.getBoundingClientRect();
  const isDropAfter = targetRect ? event.clientY > targetRect.top + targetRect.height / 2 : false;

  const [item] = newOrder.splice(fromIdx, 1);
  let insertIdx = toIdx;
  if (isDropAfter) insertIdx = toIdx + 1;
  if (fromIdx < insertIdx) insertIdx -= 1;
  newOrder.splice(insertIdx, 0, item);

  orderedOptions.value = newOrder;
  skipNextSelectClick.value = true;
  setTimeout(() => {
    skipNextSelectClick.value = false;
  }, 80);
  dragOverValue.value = null;
  draggedOption.value = null;

  const droppedOption = newOrder.find(o => o.value === overVal) ?? item;

  emit('drop', {
    draggedOption: drag,
    droppedOption,
    newOrder: newOrder.map(opt => opt.value),
  });
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

// 애니메이션 키프레임
@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ui-select {
  height: $ui-height-md; // 36px - 기본 medium 크기
  position: relative;
  font-family: $font-family;
  border-radius: $border-radius-sm;
  border: 1px solid $border-color-light;
  background: var(--color-gray-0, #fff);
  color: var(--color-gray-40, #8a949e);
  transition: all $transition-normal ease;
  box-sizing: border-box;

  // 아이콘 스타일
  &__icon {
    width: 12px;
    height: 8px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M11 1.5L6 6.5L1 1.5' stroke='%23464C53' stroke-width='2'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    transition: transform 0.2s ease;

    &--up {
      transform: rotate(180deg);
    }
  }

  // 헤더 스타일
  &__header {
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: $ui-padding-md;
    box-sizing: border-box;

    &:focus {
      outline: none;
    }
  }

  // 값 스타일
  &__value {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__placeholder {
    color: $placeholder-color;
    font-family: $placeholder-font-family;
    font-size: $ui-font-md;
    font-style: normal;
    font-weight: $placeholder-font-weight;
    line-height: $placeholder-line-height;
  }

  &__selected-text {
    color: var(--color-gray-70, #464c53);
    font-size: $ui-font-md;
  }

  // 드롭다운 — 말줄임/툴팁을 위해 패널 폭만 고정 (원본 대비 추가분)
  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow: hidden;
    z-index: 100;
    background: #fff;
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: 4px;
    margin-top: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    animation: dropdownFadeIn 0.2s ease;
    max-height: 300px;
    display: flex;
    flex-direction: column;

    &--up {
      top: auto;
      bottom: 100%;
      margin-top: 0;
      margin-bottom: 4px;
      animation: dropdownFadeInUp 0.2s ease;
    }
  }

  // 검색 컨테이너 스타일
  &__search-container {
    padding: 8px;
    border-bottom: 1px solid var(--color-gray-20, #cdd1d5);
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 2;
  }

  // 검색 인풋 스타일
  &__search-input {
    width: 100%;
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: 4px;
    outline: none;
    transition:
      border-color 0.2s,
      box-shadow 0.2s;
    box-sizing: border-box;
    padding: 6px 8px;
    font-size: 14px;

    &:focus {
      border-color: var(--color-system-b30, #0084ff);
      box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.1);
    }
  }

  // 옵션 스타일
  &__options {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 240px;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  &__option {
    cursor: pointer;
    transition: background-color 0.2s ease;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;

    &:hover {
      background-color: var(--color-gray-10, #e6e8ea);
    }

    &--selected {
      background-color: var(--color-gray-10, #e6e8ea);
      color: var(--color-system-b30, #0084ff);
      font-weight: 500;
    }

    // 말줄임·툴팁용 래퍼(타이포는 행에 상속)
    .ui-select__option-label {
      display: block;
      flex: 1 1 0;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__no-results {
    padding: 12px;
    text-align: center;
    color: var(--color-gray-40, #8a949e);
    font-size: 16px;
    font-weight: 500;
  }

  // 크기 변형 - Small (32px)
  &--small {
    height: $ui-height-sm; // 32px

    .ui-select__header {
      height: 100%;
      padding: $ui-padding-sm;
      box-sizing: border-box;
    }

    .ui-select__option {
      padding: $ui-padding-sm;
      font-size: $ui-font-sm;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-sm;
    }

    .ui-select__search-input {
      padding: $ui-padding-sm;
      font-size: $ui-font-sm;
    }
  }

  // 크기 변형 - Medium (36px)
  &--medium {
    height: $ui-height-md; // 36px

    .ui-select__header {
      height: 100%;
      padding: $ui-padding-md;
      box-sizing: border-box;
    }

    .ui-select__option {
      padding: $ui-padding-md;
      font-size: $ui-font-md;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-md;
    }

    .ui-select__search-input {
      padding: $ui-padding-md;
      font-size: $ui-font-md;
    }
  }

  // 크기 변형 - Large (48px)
  &--large {
    height: $ui-height-lg; // 48px

    .ui-select__header {
      height: 100%;
      padding: $ui-padding-lg;
      box-sizing: border-box;
    }

    .ui-select__option {
      padding: $ui-padding-lg;
      font-size: $ui-font-lg;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-lg;
    }

    .ui-select__search-input {
      padding: $ui-padding-lg;
      font-size: $ui-font-lg;
    }
  }

  // 크기 변형 - XLarge (56px)
  &--xlarge {
    height: $ui-height-xl; // 56px

    .ui-select__header {
      height: 100%;
      padding: $ui-padding-xl;
      box-sizing: border-box;
    }

    .ui-select__option {
      padding: $ui-padding-xl;
      font-size: $ui-font-xl;
    }

    .ui-select__selected-text,
    .ui-select__placeholder {
      font-size: $ui-font-xl;
    }

    .ui-select__search-input {
      padding: $ui-padding-xl;
      font-size: $ui-font-xl;
    }
  }

  // 상태 변형
  &--open {
    border: 1px solid var(--color-system-b30, #0084ff);
    color: var(--color-gray-70, #464c53);
  }

  &--selected {
    border: 1px solid var(--color-gray-60, #58616a);
    color: var(--color-gray-70, #464c53);
  }

  &--error {
    border: 1px solid var(--color-system-r30, #f30);
    color: var(--color-gray-70, #464c53);
  }

  &--disabled {
    border: 1px solid var(--color-gray-20, #cdd1d5);
    background: var(--color-gray-10, #e6e8ea);
    color: var(--color-gray-30, #b1b8be);
    cursor: not-allowed;

    .ui-select__header {
      pointer-events: none;
    }
  }

  // viewOnly 스타일을 UiInput readonly와 동일하게 수정
  &--view {
    background: var(--color-gray-10, #e6e8ea); // 읽기 전용 배경
    border: 1px solid $border-color-light;
    color: var(--color-gray-60, #58616a);
    cursor: default;

    .ui-select__header {
      pointer-events: none;
    }
  }

  // 반응형 스타일
  @media (max-width: 768px) {
    &--small {
      .ui-select__header {
        padding: 5px 10px;
      }

      .ui-select__search-input {
        padding: 5px 8px;
      }
    }

    &--medium {
      .ui-select__header {
        padding: 6px 12px;
      }

      .ui-select__search-input {
        padding: 6px 10px;
      }
    }

    &--large {
      .ui-select__header {
        padding: 8px 14px;
      }

      .ui-select__search-input {
        padding: 8px 12px;
      }
    }

    &--xlarge {
      .ui-select__header {
        padding: 10px 16px;
      }

      .ui-select__search-input {
        padding: 10px 14px;
      }
    }

    .ui-select__option {
      padding: 10px 12px;
      min-height: 44px;
    }
  }
}

// UiSelect(일반 셀렉트)와 동일한 헤더·드롭다운 스타일
.ui-searchable-select--match-ui-select {
  height: auto;

  &.ui-select--medium {
    .ui-select__header {
      height: $ui-height-md;
      padding: $ui-padding-md;
      box-sizing: border-box;
    }

    .ui-select__option {
      padding: 8px 12px;
      font-size: 14px;
      line-height: 1.4;
    }

    .ui-select__search-input {
      font-size: 14px;
    }
  }

  .ui-select__dropdown {
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

// 드래그 가능한 옵션 (보드 순서 등)
.ui-select__option--draggable {
  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    opacity: 0.7;
  }
}

.ui-select__option--drag-over {
  background-color: #e3f2fd !important;
  border-top: 2px solid #2196f3;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.ui-select__option--dragging {
  opacity: 0.5;
  transform: scale(0.95);
}

.ui-select__option {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 8px !important;
  min-width: 0;
  max-width: 100%;
}

.ui-select__checkbox {
  flex-shrink: 0;
  margin: 0;
  accent-color: var(--color-system-b30, #0084ff);
}
</style>

<!-- Teleport 드롭다운은 body에 렌더링되므로 scoped 밖 전역 스타일 필요 -->
<style lang="scss">
.ui-searchable-select__dropdown--teleported {
  background: #fff;
  border: 1px solid var(--color-gray-20, #cdd1d5);
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  animation: dropdownFadeIn 0.2s ease;

  &.ui-select__dropdown--up {
    animation: dropdownFadeInUp 0.2s ease;
  }

  .ui-select__search-container {
    padding: 8px;
    border-bottom: 1px solid var(--color-gray-20, #cdd1d5);
    flex-shrink: 0;
    background: #fff;
  }

  .ui-select__search-input {
    width: 100%;
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: 4px;
    outline: none;
    box-sizing: border-box;
    padding: 6px 8px;
    font-size: 14px;

    &:focus {
      border-color: var(--color-system-b30, #0084ff);
      box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.1);
    }
  }

  .ui-select__options {
    overflow-x: hidden;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .ui-select__option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.4;
    min-width: 0;

    &:hover {
      background-color: var(--color-gray-10, #e6e8ea);
    }

    &--selected {
      background-color: var(--color-gray-10, #e6e8ea);
      color: var(--color-system-b30, #0084ff);
      font-weight: 500;
    }
  }

  .ui-select__option-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-select__no-results {
    padding: 12px;
    text-align: center;
    color: var(--color-gray-40, #8a949e);
    font-size: 14px;
    font-weight: 500;
  }

  .ui-select__checkbox {
    flex-shrink: 0;
    margin: 0;
    accent-color: var(--color-system-b30, #0084ff);
  }
}

@keyframes dropdownFadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
